import React, { useState, useEffect, useMemo } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import {
  Search, Loader2, Download, Briefcase, MapPin, Building2,
  ChevronLeft, ChevronRight, X, AlertTriangle, Filter, ArrowUpDown
} from 'lucide-react';
import { toast } from 'sonner';
import { searchATSJobs, importATSJob } from '../services/api';

const STATUS_COLORS = {
  'Open': 'bg-green-500/10 text-green-600',
  'Active': 'bg-green-500/10 text-green-600',
  'Re-open': 'bg-green-500/10 text-green-600',
  'Sourcing': 'bg-green-500/10 text-green-600',
  'On Hold': 'bg-amber-500/10 text-amber-600',
  'Closed': 'bg-red-500/10 text-red-600',
  'Hold': 'bg-amber-500/10 text-amber-600',
  'Cancelled': 'bg-slate-500/10 text-slate-500',
};

const ATSJobImportModal = ({ isOpen, onClose, region, onImported }) => {
  const [allJobs, setAllJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [importing, setImporting] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [searchMode, setSearchMode] = useState(false);
  const [searchMeta, setSearchMeta] = useState(null);

  // Filters (applied client-side on loaded results)
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');

  // Auto-load newest jobs when modal opens
  useEffect(() => {
    if (isOpen && !loaded) {
      loadPage(1);
    }
    if (!isOpen) {
      setAllJobs([]);
      setLoaded(false);
      setSearchTerm('');
      setStatusFilter('');
      setClientFilter('');
      setSearchMode(false);
      setSearchMeta(null);
      setPage(1);
    }
  }, [isOpen]);

  const loadPage = async (p) => {
    setLoading(true);
    setSearchMode(false);
    setSearchMeta(null);
    try {
      const data = await searchATSJobs(region, '', p, 'browse');
      if (data.jobs && data.jobs.length > 0) {
        setAllJobs(data.jobs);
        setTotalPages(data.num_pages || 1);
        setTotalCount(data.count || 0);
        setPage(data.page || p);
        setLoaded(true);
        setLoading(false);
      } else if (data.loading_hint) {
        // Cache is being populated — retry after a few seconds
        setLoaded(true);
        setLoading(false);
        setTimeout(() => { retryLoad(p, 1); }, 4000);
      } else {
        setAllJobs([]);
        setLoaded(true);
        setLoading(false);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to load jobs';
      toast.error(msg);
      setLoaded(true);
      setLoading(false);
    }
  };

  const retryLoad = async (p, attempt) => {
    if (attempt > 3) return; // Max 3 retries
    setLoading(true);
    try {
      const data = await searchATSJobs(region, '', p, 'browse');
      if (data.jobs && data.jobs.length > 0) {
        setAllJobs(data.jobs);
        setTotalPages(data.num_pages || 1);
        setTotalCount(data.count || 0);
        setPage(data.page || p);
      } else if (data.loading_hint && attempt < 3) {
        setTimeout(() => { retryLoad(p, attempt + 1); }, 4000);
      }
    } catch { /* ignore retry errors */ }
    finally { setLoading(false); }
  };

  const handleDeepSearch = async () => {
    if (!searchTerm.trim()) { loadPage(1); return; }
    setSearching(true);
    setSearchMode(true);
    try {
      const data = await searchATSJobs(region, searchTerm, 1, 'search');
      setAllJobs(data.jobs || []);
      setTotalPages(1);
      setPage(1);
      setSearchMeta({ count: data.count, partial: data.partial });
    } catch (err) {
      const msg = err.response?.data?.detail || 'Search failed';
      toast.error(msg);
    } finally { setSearching(false); }
  };

  const handleImport = async (job) => {
    setImporting(job.ats_id);
    try {
      const result = await importATSJob(region, job.ats_id);
      toast.success(`Imported: ${result.title} (${result.ats_job_code})`);
      onImported();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to import job');
    } finally { setImporting(null); }
  };

  // Extract unique clients and statuses for filter dropdowns
  const uniqueClients = useMemo(() => {
    const clients = [...new Set(allJobs.map(j => j.client).filter(Boolean))].sort();
    return clients;
  }, [allJobs]);

  const uniqueStatuses = useMemo(() => {
    const statuses = [...new Set(allJobs.map(j => j.job_status).filter(Boolean))].sort();
    return statuses;
  }, [allJobs]);

  // Apply client-side filters
  const filteredJobs = useMemo(() => {
    let result = allJobs;
    if (statusFilter) {
      result = result.filter(j => j.job_status === statusFilter);
    }
    if (clientFilter) {
      result = result.filter(j => j.client === clientFilter);
    }
    // Local instant search (within loaded results, doesn't call API)
    if (searchTerm && !searchMode) {
      const s = searchTerm.toLowerCase();
      result = result.filter(j =>
        (j.job_title || '').toLowerCase().includes(s) ||
        (j.job_code || '').toLowerCase().includes(s) ||
        (j.client || '').toLowerCase().includes(s) ||
        (j.ceipal_ref || '').toLowerCase().includes(s)
      );
    }
    return result;
  }, [allJobs, statusFilter, clientFilter, searchTerm, searchMode]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-background border rounded-xl shadow-xl w-full max-w-4xl mx-4 max-h-[88vh] flex flex-col"
        onClick={e => e.stopPropagation()} data-testid="ats-import-modal">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h2 className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Import from ATS</h2>
            <p className="text-xs text-muted-foreground" data-testid="ats-total-count">
              {loaded ? `${totalCount.toLocaleString()} total jobs in Ceipal · newest first, open/active on top` : 'Loading latest jobs...'}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="ats-modal-close"><X className="h-4 w-4" /></Button>
        </div>

        {/* Search + Filters bar */}
        <div className="p-3 border-b space-y-2">
          <form onSubmit={e => { e.preventDefault(); handleDeepSearch(); }} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search by Job Code (e.g. JPC - 59058 or 59058), Ceipal ID, or exact title..."
                className="pl-9 h-9 text-sm" data-testid="ats-search-input" />
            </div>
            <Button type="submit" size="sm" disabled={searching} className="gap-1.5 h-9" data-testid="ats-deep-search-btn">
              {searching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
              Search ATS
            </Button>
            {searchMode && (
              <Button type="button" variant="outline" size="sm" className="h-9" data-testid="ats-clear-search-btn"
                onClick={() => { setSearchTerm(''); loadPage(1); }}>
                Clear search
              </Button>
            )}
          </form>

          {/* Filter row */}
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="h-3.5 w-3.5 text-muted-foreground" />
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              className="h-7 rounded-md border bg-background px-2 text-xs" data-testid="ats-status-filter">
              <option value="">All Statuses</option>
              {uniqueStatuses.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={clientFilter} onChange={e => setClientFilter(e.target.value)}
              className="h-7 rounded-md border bg-background px-2 text-xs max-w-[200px]" data-testid="ats-client-filter">
              <option value="">All Clients</option>
              {uniqueClients.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            {(statusFilter || clientFilter) && (
              <button onClick={() => { setStatusFilter(''); setClientFilter(''); }}
                className="text-xs text-muted-foreground hover:text-foreground underline">Clear filters</button>
            )}
            <span className="text-xs text-muted-foreground ml-auto">
              {filteredJobs.length} of {allJobs.length} shown
              {searchMeta && <span data-testid="ats-search-meta"> · {searchMeta.count} match{searchMeta.count === 1 ? '' : 'es'} in Ceipal{searchMeta.partial ? ' (Ceipal keyword search timed out — try the exact title or the Job Code)' : ''}</span>}
            </span>
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto">
          {(loading || searching) ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground mb-2" />
              <p className="text-xs text-muted-foreground">{searching ? 'Searching across Ceipal...' : 'Fetching jobs from Ceipal ATS...'}</p>
              <p className="text-[10px] text-muted-foreground/60 mt-1">This may take a moment on first load</p>
            </div>
          ) : filteredJobs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <AlertTriangle className="h-8 w-8 mb-2 opacity-30" />
              <p className="text-sm">{allJobs.length === 0 ? 'No jobs loaded yet' : 'No jobs match your filters'}</p>
              {(statusFilter || clientFilter) && (
                <button onClick={() => { setStatusFilter(''); setClientFilter(''); }}
                  className="text-xs text-primary mt-2 underline">Clear filters</button>
              )}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/30 sticky top-0">
                <tr className="text-xs text-muted-foreground">
                  <th className="text-left p-2 pl-4 font-medium">Job Title</th>
                  <th className="text-left p-2 font-medium">Code</th>
                  <th className="text-left p-2 font-medium">Client</th>
                  <th className="text-left p-2 font-medium">Status</th>
                  <th className="text-left p-2 font-medium">Location</th>
                  <th className="text-left p-2 font-medium">Type</th>
                  <th className="text-right p-2 pr-4 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredJobs.map((job) => (
                  <tr key={job.ats_id} className="border-b hover:bg-muted/20 transition-colors" data-testid={`ats-job-row-${job.ats_id}`}>
                    <td className="p-2 pl-4">
                      <p className="font-medium text-sm truncate max-w-[220px]">{job.job_title}</p>
                      {job.primary_skills && <p className="text-[10px] text-muted-foreground truncate max-w-[220px]">{job.primary_skills}</p>}
                    </td>
                    <td className="p-2">
                      <Badge variant="outline" className="text-[10px] font-mono whitespace-nowrap" data-testid={`ats-job-code-${job.ats_id}`}>{job.job_code}</Badge>
                    </td>
                    <td className="p-2">
                      <span className="text-xs truncate max-w-[120px] block">{job.client || '—'}</span>
                    </td>
                    <td className="p-2">
                      <Badge className={`text-[10px] ${STATUS_COLORS[job.job_status] || 'bg-slate-500/10 text-slate-500'}`}>{job.job_status || '—'}</Badge>
                    </td>
                    <td className="p-2">
                      <span className="text-xs text-muted-foreground truncate max-w-[120px] block">
                        {[job.city, job.state].filter(Boolean).join(', ') || '—'}
                      </span>
                    </td>
                    <td className="p-2">
                      <span className="text-xs">{job.job_type || '—'}</span>
                    </td>
                    <td className="p-2 pr-4 text-right">
                      <Button size="sm" className="gap-1 h-7 text-xs" onClick={() => handleImport(job)}
                        disabled={importing === job.ats_id} data-testid={`import-btn-${job.ats_id}`}>
                        {importing === job.ats_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
                        Import
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination (only in browse mode) */}
        {!searchMode && loaded && totalPages > 1 && (
          <div className="flex items-center justify-between p-3 border-t">
            <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => loadPage(page - 1)} className="h-7 text-xs" data-testid="ats-prev-page-btn">
              <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Previous
            </Button>
            <span className="text-xs text-muted-foreground" data-testid="ats-page-indicator">Page {page} of {totalPages.toLocaleString()}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages || loading} onClick={() => loadPage(page + 1)} className="h-7 text-xs" data-testid="ats-next-page-btn">
              Next <ChevronRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ATSJobImportModal;
